import { useEffect, useRef, useCallback } from 'react';

/**
 * Custom hook to bind chat-specific socket events.
 * Keeps event registration decoupled from UI components.
 *
 * @param {object} socket  - socket.io-client instance (may be null while connecting)
 * @param {object} handlers - map of event → handler function
 */
export const useChatSocket = (socket, handlers) => {
  const handlersRef = useRef(handlers);

  // Always keep the ref up-to-date so stale closures aren't an issue
  useEffect(() => {
    handlersRef.current = handlers;
  });

  useEffect(() => {
    if (!socket) return;

    const boundHandlers = {};

    Object.keys(handlersRef.current).forEach((event) => {
      const bound = (...args) => handlersRef.current[event]?.(...args);
      boundHandlers[event] = bound;
      socket.on(event, bound);
    });

    return () => {
      Object.entries(boundHandlers).forEach(([event, bound]) => {
        socket.off(event, bound);
      });
    };
  }, [socket]);
};

/**
 * Returns a stable emit callback that is safe to call even before
 * the socket is fully connected.
 */
export const useSocketEmit = (socket) => {
  return useCallback(
    (event, payload, ack) => {
      if (!socket?.connected) return;
      if (typeof ack === 'function') {
        socket.emit(event, payload, ack);
      } else {
        socket.emit(event, payload);
      }
    },
    [socket]
  );
};
